import { useState, useEffect, useRef, FormEvent } from "react";
import {
  ShoppingBag,
  ShoppingCart,
  Link2,
  Plus,
  Trash2,
  Save,
  Eye,
  EyeOff,
  CheckCircle2,
  Search,
  Star,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Info,
  Building2,
  Package,
  X,
} from "lucide-react";
import { affiliates, agentTasks, sites as sitesApi } from "../lib/api";
import type {
  AffiliateConfig,
  AwinProgram,
  CjAdvertiser,
  LiveProduct,
  LiveProductNetwork,
  AffiliateProduct,
} from "../lib/api";
import Modal from "../components/Modal";

type TabId = "ebay" | "amazon" | "awin" | "cj" | "custom";

const TABS: { id: TabId; label: string; color: string }[] = [
  { id: "ebay", label: "eBay Partner Network", color: "amber" },
  { id: "amazon", label: "Amazon Associates", color: "orange" },
  { id: "awin", label: "AWIN", color: "blue" },
  { id: "cj", label: "Commission Junction", color: "green" },
  { id: "custom", label: "Custom", color: "purple" },
];

const POLICIES: Record<TabId, { title: string; items: string[] }> = {
  ebay: {
    title: "eBay Partner Network Compliance",
    items: [
      "Display eBay branding and logo where required.",
      "Do not use misleading claims about item availability or pricing.",
      "Prices change frequently — do not cache prices for more than 24 hours.",
      "Include a clear affiliate disclosure near eBay links.",
    ],
  },
  amazon: {
    title: "Amazon Associates Compliance",
    items: [
      "Display the Associates disclosure on every page with Amazon links.",
      "Do not quote exact prices — direct users to Amazon for current pricing.",
      "Product images must link to Amazon. Do not save or rehost images.",
      "Do not use Amazon links in email, ebooks, or PDFs.",
    ],
  },
  awin: {
    title: "AWIN Publisher Compliance",
    items: [
      "Comply with each individual advertiser's programme terms.",
      "Do not bid on trademarked terms unless the advertiser permits it.",
      "Include a clear affiliate disclosure on all pages with AWIN links.",
      "Product data must be refreshed at least weekly per AWIN terms.",
    ],
  },
  cj: {
    title: "Commission Junction Compliance",
    items: [
      "Review and comply with each advertiser's individual program terms.",
      "Include an FTC-compliant affiliate disclosure on all content.",
      "Do not modify deep-link URLs or strip tracking parameters.",
      "Comply with CJ's Publisher Service Agreement at all times.",
    ],
  },
  custom: {
    title: "Custom Program Guidelines",
    items: [
      "Review the individual terms for each custom affiliate program.",
      "Include a clear affiliate disclosure on all pages with affiliate links.",
    ],
  },
};

function PolicyBox({ tab }: { tab: TabId }) {
  const p = POLICIES[tab];
  return (
    <div className="flex gap-3 px-4 py-3 bg-blue-500/5 border border-blue-500/15 rounded-lg">
      <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-xs font-semibold text-blue-300 mb-1">{p.title}</p>
        <ul className="space-y-0.5">
          {p.items.map((item) => (
            <li key={item} className="text-xs text-blue-200/70 flex gap-1.5">
              <span className="text-blue-400 mt-0.5">·</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ProductCard({
  product,
  promoted,
  selected,
  onPromote,
  onUnpromote,
  onToggleSelect,
  busy,
}: {
  product: LiveProduct;
  promoted: boolean;
  selected?: boolean;
  onPromote: () => void;
  onUnpromote: () => void;
  onToggleSelect?: () => void;
  busy: boolean;
}) {
  return (
    <div className={`bg-gray-800/50 border rounded-xl overflow-hidden flex flex-col group ${selected ? "border-brand-500 ring-1 ring-brand-500/50" : "border-gray-700"}`}>
      <div className="relative aspect-square bg-gray-800">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.title}
            className="w-full h-full object-contain p-2"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-8 h-8 text-gray-600" />
          </div>
        )}
        {onToggleSelect && (
          <button
            onClick={onToggleSelect}
            className={`absolute top-2 left-2 w-5 h-5 rounded border flex items-center justify-center transition-all ${
              selected
                ? "bg-brand-500 border-brand-500 text-white"
                : "bg-gray-900/80 border-gray-600 text-transparent opacity-0 group-hover:opacity-100"
            }`}
            title={selected ? "Deselect" : "Select for content creation"}
          >
            {selected && <CheckCircle2 className="w-3.5 h-3.5" />}
          </button>
        )}
        <button
          onClick={promoted ? onUnpromote : onPromote}
          disabled={busy}
          className={`absolute top-2 right-2 p-1.5 rounded-full transition-all ${
            promoted
              ? "bg-brand-500 text-white"
              : "bg-gray-900/80 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-brand-400"
          } disabled:opacity-50`}
          title={promoted ? "Remove from promoted" : "Promote this product"}
        >
          <Star className="w-3.5 h-3.5" fill={promoted ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <p className="text-xs font-medium text-gray-100 line-clamp-2 leading-snug">{product.title}</p>
        {product.merchantName && (
          <p className="text-[11px] text-gray-500">{product.merchantName}</p>
        )}
        <div className="flex items-center justify-between mt-auto pt-1.5">
          <span className="text-sm font-semibold text-green-400">
            {product.price != null
              ? `${product.currency ?? "$"}${product.price.toFixed(2)}`
              : "—"}
          </span>
          <a
            href={product.productUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
        {product.category && (
          <p className="text-[10px] text-gray-600 truncate">{product.category}</p>
        )}
      </div>
    </div>
  );
}

function PromotedBadge({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span className="ml-1.5 px-1.5 py-0.5 bg-brand-500/20 text-brand-300 text-[10px] font-medium rounded-full">
      {count}
    </span>
  );
}

// ─── Main Component ──────────────────────────────────────────

export default function Affiliates() {
  const [tab, setTab] = useState<TabId>("ebay");
  const [config, setConfig] = useState<AffiliateConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Promoted products per network
  const [promoted, setPromoted] = useState<Record<string, AffiliateProduct>>({});
  const [promoteBusy, setPromoteBusy] = useState<Record<string, boolean>>({});

  // Live product search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<LiveProduct[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AWIN programs
  const [awinPrograms, setAwinPrograms] = useState<AwinProgram[]>([]);
  const [awinProgramsLoading, setAwinProgramsLoading] = useState(false);

  // CJ advertisers
  const [cjAdvertisers, setCjAdvertisers] = useState<CjAdvertiser[]>([]);
  const [cjAdvertisersLoading, setCjAdvertisersLoading] = useState(false);

  // Credential forms
  const [showKey, setShowKey] = useState(false);
  const [ebayForm, setEbayForm] = useState({ appId: "", certId: "", devId: "" });
  const [savingEbay, setSavingEbay] = useState(false);
  const [amazonForm, setAmazonForm] = useState({ accessKey: "", secretKey: "", region: "us-east-1" });
  const [savingAmazon, setSavingAmazon] = useState(false);
  const [awinForm, setAwinForm] = useState({ publisherId: "", apiToken: "", advertiserId: "" });
  const [savingAwin, setSavingAwin] = useState(false);
  const [cjForm, setCjForm] = useState({ cid: "", apiKey: "", advertiserId: "" });
  const [savingCj, setSavingCj] = useState(false);

  // Bulk content creation
  const [createContentBusy, setCreateContentBusy] = useState(false);
  const [siteList, setSiteList] = useState<{ _id: string; domain: string }[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>("");
  const [showSitePicker, setShowSitePicker] = useState(false);
  const [pendingProducts, setPendingProducts] = useState<LiveProduct[]>([]);

  // Custom programs
  const [customModal, setCustomModal] = useState(false);
  const [customForm, setCustomForm] = useState({ name: "", baseUrl: "", affiliateId: "", commissionRate: "" });
  const [savingCustom, setSavingCustom] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);
  const [deleteCustom, setDeleteCustom] = useState<string | null>(null);

  useEffect(() => {
    void loadAll();
  }, []);

  useEffect(() => {
    // Load promoted products for the current tab
    if (tab !== "custom") {
      void loadPromoted(tab as LiveProductNetwork);
    }
    // Reset search when switching tabs
    setSearchResults([]);
    setSearchQuery("");
    setSearchError(null);
    // Auto-load programmes/advertisers and browse products when switching tabs
    if (tab === "awin" && config?.awin && awinPrograms.length === 0 && !awinProgramsLoading) {
      void loadAwinPrograms();
    }
    if (tab === "cj" && config?.cj?.cid && config.cj.apiKey && cjAdvertisers.length === 0 && !cjAdvertisersLoading) {
      void loadCjAdvertisers();
    }
    // Auto-load browse products for all tabs (search with empty = popular/all)
    if (tab !== "custom" && searchResults.length === 0 && !searching) {
      if (tab === "ebay" && config?.ebay) {
        triggerSearch("ebay", config.ebay.appId ? "popular" : "");
      } else if (tab === "awin" && config?.awin) {
        triggerSearch("awin", "popular");
      } else if (tab === "cj" && config?.cj?.cid && config.cj.apiKey) {
        triggerSearch("cj", "popular");
      }
    }
  }, [tab, config]);

  async function loadAll() {
    setLoading(true);
    try {
      const aff = await affiliates.get();
      setConfig(aff.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  async function loadPromoted(network: LiveProductNetwork) {
    try {
      const res = await affiliates.listPromoted(network);
      const map: Record<string, AffiliateProduct> = {};
      for (const p of res.data) {
        if (p.promoted) map[p.externalId] = p;
      }
      setPromoted((prev) => ({ ...prev, ...map }));
    } catch {
      // non-fatal
    }
  }

  function showSuccess(msg: string) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }

  function triggerSearch(network: LiveProductNetwork, q: string) {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {
        const res = await affiliates.liveSearch(network, q);
        setSearchResults(res.data);
      } catch (err: unknown) {
        setSearchError(err instanceof Error ? err.message : "Search failed");
      } finally {
        setSearching(false);
      }
    }, 500);
  }

  function onSearchChange(network: LiveProductNetwork, value: string) {
    setSearchQuery(value);
    triggerSearch(network, value);
  }

  async function handlePromote(product: LiveProduct) {
    const key = product.externalId;
    setPromoteBusy((b) => ({ ...b, [key]: true }));
    try {
      await affiliates.promote(product);
      setPromoted((p) => ({ ...p, [key]: { ...product, _id: key, promoted: true } as unknown as AffiliateProduct }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to promote");
    } finally {
      setPromoteBusy((b) => ({ ...b, [key]: false }));
    }
  }

  async function handleUnpromote(product: LiveProduct) {
    const key = product.externalId;
    setPromoteBusy((b) => ({ ...b, [key]: true }));
    try {
      await affiliates.unpromote(product.externalId, product.network);
      setPromoted((p) => { const n = { ...p }; delete n[key]; return n; });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to unpromote");
    } finally {
      setPromoteBusy((b) => ({ ...b, [key]: false }));
    }
  }

  async function loadAwinPrograms() {
    setAwinProgramsLoading(true);
    try {
      const res = await affiliates.listAwinPrograms();
      setAwinPrograms(res.data);
    } catch (err: unknown) {
      setSearchError(err instanceof Error ? err.message : "Failed to load programs");
    } finally {
      setAwinProgramsLoading(false);
    }
  }

  async function loadCjAdvertisers() {
    setCjAdvertisersLoading(true);
    try {
      const res = await affiliates.listCjAdvertisers();
      setCjAdvertisers(res.data);
    } catch (err: unknown) {
      setSearchError(err instanceof Error ? err.message : "Failed to load advertisers");
    } finally {
      setCjAdvertisersLoading(false);
    }
  }

  // Load sites for the site picker
  useEffect(() => {
    sitesApi.list().then((res) => {
      setSiteList(res.data.map((s: { _id: string; domain: string }) => ({ _id: s._id, domain: s.domain })));
      if (res.data.length === 1) setSelectedSiteId(res.data[0]._id);
    }).catch(() => { /* non-fatal */ });
  }, []);

  function handleCreateContent(products: LiveProduct[]) {
    if (siteList.length === 0) {
      setError("No sites available. Create a site first.");
      return;
    }
    if (siteList.length === 1) {
      void submitContentTasks(siteList[0]._id, products);
    } else {
      setPendingProducts(products);
      setShowSitePicker(true);
    }
  }

  async function submitContentTasks(siteId: string, products: LiveProduct[]) {
    setCreateContentBusy(true);
    setShowSitePicker(false);
    try {
      const keywords = products.map((p) => p.title.replace(/[^a-zA-Z0-9\s]/g, "").trim().slice(0, 80));
      await agentTasks.runPipeline({
        siteId,
        maxPages: keywords.length,
        keywords,
      });
      showSuccess(`Queued ${keywords.length} content page${keywords.length > 1 ? "s" : ""} for creation`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to queue content");
    } finally {
      setCreateContentBusy(false);
    }
  }

  // Credential save handlers
  async function saveEbay(e: FormEvent) {
    e.preventDefault();
    setSavingEbay(true);
    try {
      await affiliates.updateEbay({
        appId: ebayForm.appId,
        ...(ebayForm.certId ? { certId: ebayForm.certId } : {}),
        ...(ebayForm.devId ? { devId: ebayForm.devId } : {}),
      });
      showSuccess("eBay credentials saved");
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSavingEbay(false);
    }
  }

  async function saveAmazon(e: FormEvent) {
    e.preventDefault();
    setSavingAmazon(true);
    try {
      await affiliates.updateAmazon({
        accessKey: amazonForm.accessKey,
        secretKey: amazonForm.secretKey,
        ...(amazonForm.region ? { region: amazonForm.region } : {}),
      });
      showSuccess("Amazon credentials saved");
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSavingAmazon(false);
    }
  }

  async function saveAwin(e: FormEvent) {
    e.preventDefault();
    setSavingAwin(true);
    try {
      await affiliates.updateAwin({
        publisherId: awinForm.publisherId,
        apiToken: awinForm.apiToken || undefined,
        advertiserId: awinForm.advertiserId || undefined,
      });
      showSuccess("AWIN credentials saved");
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSavingAwin(false);
    }
  }

  async function saveCj(e: FormEvent) {
    e.preventDefault();
    setSavingCj(true);
    try {
      await affiliates.updateCj({
        cid: cjForm.cid,
        apiKey: cjForm.apiKey,
        ...(cjForm.advertiserId ? { advertiserId: cjForm.advertiserId } : {}),
      });
      showSuccess("CJ credentials saved");
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSavingCj(false);
    }
  }

  async function saveCustom(e: FormEvent) {
    e.preventDefault();
    setSavingCustom(true);
    setCustomError(null);
    try {
      await affiliates.addCustom({
        name: customForm.name,
        baseUrl: customForm.baseUrl,
        affiliateId: customForm.affiliateId,
        commissionRate: customForm.commissionRate ? parseFloat(customForm.commissionRate) : undefined,
      });
      setCustomModal(false);
      showSuccess("Custom program added");
      await loadAll();
    } catch (err: unknown) {
      setCustomError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSavingCustom(false);
    }
  }

  async function handleDeleteCustom() {
    if (!deleteCustom) return;
    try {
      await affiliates.removeCustom(deleteCustom);
      setDeleteCustom(null);
      showSuccess("Program removed");
      await loadAll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  }

  const promotedCount = (network: LiveProductNetwork) =>
    Object.values(promoted).filter((p) => p.provider === network && p.promoted).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-100">Affiliate Networks</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Connect your affiliate accounts, browse products, and manage what you promote.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {successMsg && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-lg text-sm text-green-400">
          <CheckCircle2 className="w-4 h-4" />
          {successMsg}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-800 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setTab(t.id);
              setSearchQuery("");
              setSearchResults([]);
              setSearchError(null);
            }}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id
                ? "border-brand-500 text-brand-400"
                : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {t.id === "ebay" && <ShoppingBag className="w-4 h-4" />}
            {t.id === "amazon" && <ShoppingCart className="w-4 h-4" />}
            {t.id === "awin" && <span className="text-xs font-bold">Aw</span>}
            {t.id === "cj" && <Building2 className="w-4 h-4" />}
            {t.id === "custom" && <Link2 className="w-4 h-4" />}
            {t.label}
            {t.id !== "custom" && (
              <PromotedBadge count={promotedCount(t.id as LiveProductNetwork)} />
            )}
          </button>
        ))}
      </div>

      {/* ─── eBay Tab ─────────────────────────────────────────── */}
      {tab === "ebay" && (
        <div className="space-y-5">
          <PolicyBox tab="ebay" />

          {/* Credentials */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-800">
              <ShoppingBag className="w-4 h-4 text-amber-400" />
              <p className="text-sm font-semibold text-gray-100">EPN Credentials</p>
              {config?.ebay && <span className="ml-auto text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Connected</span>}
            </div>
            <form onSubmit={saveEbay} className="p-5 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">App ID (Client ID) *</label>
                <div className="relative">
                  <input value={ebayForm.appId} onChange={(e) => setEbayForm((f) => ({ ...f, appId: e.target.value }))}
                    type={showKey ? "text" : "password"}
                    placeholder={config?.ebay?.appId || "YourApp-PRD-xxx"}
                    className="w-full px-3 py-2 pr-9 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  <button type="button" onClick={() => setShowKey((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Cert ID</label>
                <input value={ebayForm.certId} onChange={(e) => setEbayForm((f) => ({ ...f, certId: e.target.value }))}
                  type={showKey ? "text" : "password"} placeholder={config?.ebay?.certId || "PRD-xxx"}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Dev ID</label>
                <input value={ebayForm.devId} onChange={(e) => setEbayForm((f) => ({ ...f, devId: e.target.value }))}
                  type={showKey ? "text" : "password"} placeholder={config?.ebay?.devId || "xxxxxxxx-xxxx"}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="col-span-2 flex justify-end">
                <button type="submit" disabled={savingEbay || !ebayForm.appId}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                  <Save className="w-4 h-4" />
                  {savingEbay ? "Saving…" : "Save EPN credentials"}
                </button>
              </div>
            </form>
          </div>

          {/* Product search */}
          {config?.ebay && <ProductSearchPanel network="ebay" searchQuery={searchQuery} searchResults={searchResults}
            searching={searching} searchError={searchError} promoted={promoted} promoteBusy={promoteBusy}
            onSearchChange={(v) => onSearchChange("ebay", v)} onPromote={handlePromote} onUnpromote={handleUnpromote}
            onCreateContent={handleCreateContent} createContentBusy={createContentBusy} />}
        </div>
      )}

      {/* ─── Amazon Tab ───────────────────────────────────────── */}
      {tab === "amazon" && (
        <div className="space-y-5">
          <PolicyBox tab="amazon" />

          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-800">
              <ShoppingCart className="w-4 h-4 text-orange-400" />
              <p className="text-sm font-semibold text-gray-100">Amazon PA API Credentials</p>
              {config?.amazon && <span className="ml-auto text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Connected</span>}
            </div>
            <div className="px-5 pt-3 pb-1">
              <p className="text-xs text-gray-500">PA API credentials. Tracking ID is configured per-website in the Affiliates tab.</p>
            </div>
            <form onSubmit={saveAmazon} className="p-5 pt-2 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Region</label>
                <select value={amazonForm.region} onChange={(e) => setAmazonForm((f) => ({ ...f, region: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="us-east-1">US (us-east-1)</option>
                  <option value="eu-west-1">EU (eu-west-1)</option>
                  <option value="us-west-2">CA (us-west-2)</option>
                  <option value="ap-northeast-1">JP (ap-northeast-1)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Access Key *</label>
                <input value={amazonForm.accessKey} onChange={(e) => setAmazonForm((f) => ({ ...f, accessKey: e.target.value }))}
                  type={showKey ? "text" : "password"} placeholder={config?.amazon?.accessKey || "AKIAIO…"}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Secret Key *</label>
                <div className="relative">
                  <input value={amazonForm.secretKey} onChange={(e) => setAmazonForm((f) => ({ ...f, secretKey: e.target.value }))}
                    type={showKey ? "text" : "password"} placeholder={config?.amazon?.secretKey || "wJalrXUtn…"}
                    className="w-full px-3 py-2 pr-9 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  <button type="button" onClick={() => setShowKey((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="col-span-2 flex justify-end">
                <button type="submit" disabled={savingAmazon || !amazonForm.accessKey || !amazonForm.secretKey}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                  <Save className="w-4 h-4" />
                  {savingAmazon ? "Saving…" : "Save Amazon credentials"}
                </button>
              </div>
            </form>
          </div>

          {!config?.amazon ? (
            <div className="px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-xs text-gray-400">
              Amazon PA API product search is available once credentials are saved. Products promote using
              keyword-based search links (no direct ASINs) to comply with Amazon's real-time pricing rules.
            </div>
          ) : (
            <div className="px-4 py-3 bg-green-900/20 border border-green-800/40 rounded-lg text-xs text-green-400">
              Amazon PA API connected. Products use keyword-based search links (no direct ASINs) to comply with Amazon's real-time pricing rules.
            </div>
          )}
        </div>
      )}

      {/* ─── AWIN Tab ─────────────────────────────────────────── */}
      {tab === "awin" && (
        <div className="space-y-5">
          <PolicyBox tab="awin" />

          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-800">
              <span className="text-sm font-bold text-blue-400">Aw</span>
              <p className="text-sm font-semibold text-gray-100">AWIN Credentials</p>
              {config?.awin && <span className="ml-auto text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Connected · Publisher {config.awin.publisherId}</span>}
            </div>
            <form onSubmit={saveAwin} className="p-5 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Publisher ID *</label>
                <input value={awinForm.publisherId} onChange={(e) => setAwinForm((f) => ({ ...f, publisherId: e.target.value }))}
                  placeholder={config?.awin?.publisherId || "123456"}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Default Advertiser ID</label>
                <input value={awinForm.advertiserId} onChange={(e) => setAwinForm((f) => ({ ...f, advertiserId: e.target.value }))}
                  placeholder={config?.awin?.advertiserId || "e.g. 12345"}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-400 mb-1.5">API Token</label>
                <div className="relative">
                  <input value={awinForm.apiToken} onChange={(e) => setAwinForm((f) => ({ ...f, apiToken: e.target.value }))}
                    type={showKey ? "text" : "password"} placeholder={config?.awin?.apiToken ? "••••••••" : "Your AWIN API token"}
                    className="w-full px-3 py-2 pr-9 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  <button type="button" onClick={() => setShowKey((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="col-span-2 flex justify-end">
                <button type="submit" disabled={savingAwin || !awinForm.publisherId}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                  <Save className="w-4 h-4" />
                  {savingAwin ? "Saving…" : "Save AWIN keys"}
                </button>
              </div>
            </form>
          </div>

          {/* Advertiser programs */}
          {config?.awin && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-800">
                <p className="text-sm font-semibold text-gray-100">Joined Programmes</p>
                <button onClick={loadAwinPrograms} disabled={awinProgramsLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg border border-gray-700 transition-colors disabled:opacity-50">
                  <RefreshCw className={`w-3.5 h-3.5 ${awinProgramsLoading ? "animate-spin" : ""}`} />
                  {awinProgramsLoading ? "Loading…" : "Load"}
                </button>
              </div>
              {awinPrograms.length > 0 ? (
                <div className="divide-y divide-gray-800 max-h-64 overflow-y-auto">
                  {awinPrograms.map((prog) => (
                    <div key={prog.id} className="flex items-center gap-3 px-5 py-3">
                      {prog.logoUrl ? (
                        <img src={prog.logoUrl} alt={prog.name} className="w-8 h-8 object-contain rounded" />
                      ) : (
                        <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-gray-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-200 truncate">{prog.name}</p>
                        <p className="text-xs text-gray-500">{prog.primarySector} · {prog.primaryRegion}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        prog.membershipStatus === "accepted" ? "bg-green-500/10 text-green-400" : "bg-gray-700 text-gray-400"
                      }`}>{prog.membershipStatus}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-5 py-4 text-xs text-gray-600 italic">Click Load to fetch your joined programmes.</p>
              )}
            </div>
          )}

          {/* Product search from synced products */}
          {config?.awin && <ProductSearchPanel network="awin" searchQuery={searchQuery} searchResults={searchResults}
            searching={searching} searchError={searchError} promoted={promoted} promoteBusy={promoteBusy}
            onSearchChange={(v) => onSearchChange("awin", v)} onPromote={handlePromote} onUnpromote={handleUnpromote}
            onCreateContent={handleCreateContent} createContentBusy={createContentBusy}
            helpText="Search your synced AWIN product catalog. Sync products first via the per-website Affiliates tab." />}
        </div>
      )}

      {/* ─── CJ Tab ───────────────────────────────────────────── */}
      {tab === "cj" && (
        <div className="space-y-5">
          <PolicyBox tab="cj" />

          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-800">
              <Building2 className="w-4 h-4 text-green-400" />
              <p className="text-sm font-semibold text-gray-100">CJ Credentials</p>
              {config?.cj && <span className="ml-auto text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Connected · CID {config.cj.cid}</span>}
            </div>
            <div className="px-5 pt-3 pb-1">
              <p className="text-xs text-gray-500">Account credentials. Website ID is configured per-website in the Affiliates tab.</p>
            </div>
            <form onSubmit={saveCj} className="p-5 pt-2 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Company ID (CID) *</label>
                <input value={cjForm.cid} onChange={(e) => setCjForm((f) => ({ ...f, cid: e.target.value }))}
                  placeholder={config?.cj?.cid || "1234567"}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Default Advertiser ID</label>
                <input value={cjForm.advertiserId} onChange={(e) => setCjForm((f) => ({ ...f, advertiserId: e.target.value }))}
                  placeholder={config?.cj?.advertiserId || "e.g. 3382718"}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Personal Access Token (PAT) *</label>
                <div className="relative">
                  <input value={cjForm.apiKey} onChange={(e) => setCjForm((f) => ({ ...f, apiKey: e.target.value }))}
                    type={showKey ? "text" : "password"} placeholder={config?.cj?.apiKey ? "••••••••" : "Your CJ API token"}
                    className="w-full px-3 py-2 pr-9 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  <button type="button" onClick={() => setShowKey((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="col-span-2 flex justify-end">
                <button type="submit" disabled={savingCj || !cjForm.cid || !cjForm.apiKey}
                  className="flex items-center gap-2 px-4 py-2 bg-green-700 hover:bg-green-800 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                  <Save className="w-4 h-4" />
                  {savingCj ? "Saving…" : "Save CJ keys"}
                </button>
              </div>
            </form>
          </div>

          {/* Joined advertisers */}
          {config?.cj && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-800">
                <p className="text-sm font-semibold text-gray-100">Joined Advertisers</p>
                <button onClick={loadCjAdvertisers} disabled={cjAdvertisersLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg border border-gray-700 transition-colors disabled:opacity-50">
                  <RefreshCw className={`w-3.5 h-3.5 ${cjAdvertisersLoading ? "animate-spin" : ""}`} />
                  {cjAdvertisersLoading ? "Loading…" : "Load"}
                </button>
              </div>
              {cjAdvertisers.length > 0 ? (
                <div className="divide-y divide-gray-800 max-h-64 overflow-y-auto">
                  {cjAdvertisers.map((adv) => (
                    <div key={adv.id} className="flex items-center gap-3 px-5 py-3">
                      {adv.logoUrl ? (
                        <img src={adv.logoUrl} alt={adv.name} className="w-8 h-8 object-contain rounded" />
                      ) : (
                        <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-gray-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-200 truncate">{adv.name}</p>
                        <p className="text-xs text-gray-500">{adv.primaryRegion}</p>
                      </div>
                      {adv.clickCommission && (
                        <span className="text-xs text-green-400 font-medium">{adv.clickCommission}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-5 py-4 text-xs text-gray-600 italic">Click Load to fetch your joined advertisers.</p>
              )}
            </div>
          )}

          {config?.cj && <ProductSearchPanel network="cj" searchQuery={searchQuery} searchResults={searchResults}
            searching={searching} searchError={searchError} promoted={promoted} promoteBusy={promoteBusy}
            onSearchChange={(v) => onSearchChange("cj", v)} onPromote={handlePromote} onUnpromote={handleUnpromote}
            onCreateContent={handleCreateContent} createContentBusy={createContentBusy} />}
        </div>
      )}

      {/* ─── Custom Tab ───────────────────────────────────────── */}
      {tab === "custom" && (
        <div className="space-y-5">
          <PolicyBox tab="custom" />

          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <Link2 className="w-4 h-4 text-purple-400" />
                <p className="text-sm font-semibold text-gray-100">Custom Programs</p>
              </div>
              <button onClick={() => { setCustomForm({ name: "", baseUrl: "", affiliateId: "", commissionRate: "" }); setCustomModal(true); }}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium rounded-lg border border-gray-700 transition-colors">
                <Plus className="w-3.5 h-3.5" />
                Add Program
              </button>
            </div>

            {(!config?.custom || config.custom.length === 0) ? (
              <div className="p-8 text-center">
                <Link2 className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                <p className="text-sm text-gray-600">No custom programs yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {config.custom.map((program) => (
                  <div key={program.name} className="flex items-center justify-between px-5 py-3.5">
                    <div>
                      <p className="text-sm font-medium text-gray-200">{program.name}</p>
                      <p className="text-xs text-gray-500">
                        {program.baseUrl} · ID: {program.affiliateId}
                        {program.commissionRate && ` · ${program.commissionRate}%`}
                      </p>
                    </div>
                    <button onClick={() => setDeleteCustom(program.name)}
                      className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom program modal */}
      <Modal open={customModal} onClose={() => setCustomModal(false)} title="Add Custom Program" size="md">
        <form onSubmit={saveCustom} className="space-y-4">
          {customError && <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">{customError}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Program Name *</label>
              <input value={customForm.name} onChange={(e) => setCustomForm((f) => ({ ...f, name: e.target.value }))}
                required placeholder="e.g. Jasper AI"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Commission %</label>
              <input value={customForm.commissionRate} onChange={(e) => setCustomForm((f) => ({ ...f, commissionRate: e.target.value }))}
                type="number" min="0" max="100" step="0.1" placeholder="30"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Base URL *</label>
              <input value={customForm.baseUrl} onChange={(e) => setCustomForm((f) => ({ ...f, baseUrl: e.target.value }))}
                required placeholder="https://jasper.ai/?fpr="
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Affiliate ID *</label>
              <input value={customForm.affiliateId} onChange={(e) => setCustomForm((f) => ({ ...f, affiliateId: e.target.value }))}
                required placeholder="your-affiliate-id"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setCustomModal(false)}
              className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={savingCustom}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              {savingCustom ? "Saving…" : "Add program"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete custom confirm */}
      <Modal open={!!deleteCustom} onClose={() => setDeleteCustom(null)} title="Remove Program" size="sm">
        <p className="text-sm text-gray-400 mb-5">Remove <strong className="text-gray-200">{deleteCustom}</strong>?</p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setDeleteCustom(null)} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors">Cancel</button>
          <button onClick={handleDeleteCustom} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors">Remove</button>
        </div>
      </Modal>

      {/* Site picker for bulk content creation */}
      <Modal open={showSitePicker} onClose={() => setShowSitePicker(false)} title="Select Website" size="sm">
        <p className="text-sm text-gray-400 mb-4">
          Create {pendingProducts.length} content page{pendingProducts.length > 1 ? "s" : ""} on which website?
        </p>
        <div className="space-y-2 mb-5">
          {siteList.map((site) => (
            <button key={site._id} onClick={() => setSelectedSiteId(site._id)}
              className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                selectedSiteId === site._id
                  ? "border-brand-500 bg-brand-900/30 text-brand-200"
                  : "border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-600"
              }`}>
              {site.domain}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => setShowSitePicker(false)}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors">Cancel</button>
          <button onClick={() => void submitContentTasks(selectedSiteId, pendingProducts)}
            disabled={!selectedSiteId || createContentBusy}
            className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
            {createContentBusy ? "Queueing…" : "Create Content"}
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ─── Product Search Panel ────────────────────────────────────

function ProductSearchPanel({
  network,
  searchQuery,
  searchResults,
  searching,
  searchError,
  promoted,
  promoteBusy,
  onSearchChange,
  onPromote,
  onUnpromote,
  onCreateContent,
  createContentBusy,
  helpText,
}: {
  network: LiveProductNetwork;
  searchQuery: string;
  searchResults: LiveProduct[];
  searching: boolean;
  searchError: string | null;
  promoted: Record<string, AffiliateProduct>;
  promoteBusy: Record<string, boolean>;
  onSearchChange: (v: string) => void;
  onPromote: (p: LiveProduct) => void;
  onUnpromote: (p: LiveProduct) => void;
  onCreateContent?: (products: LiveProduct[]) => void;
  createContentBusy?: boolean;
  helpText?: string;
}) {
  const [selected, setSelected] = useState<Record<string, LiveProduct>>({});
  const selectedCount = Object.keys(selected).length;

  const toggleSelect = (p: LiveProduct) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[p.externalId]) {
        delete next[p.externalId];
      } else {
        next[p.externalId] = p;
      }
      return next;
    });
  };

  const selectAll = () => {
    const map: Record<string, LiveProduct> = {};
    for (const p of searchResults) map[p.externalId] = p;
    setSelected(map);
  };

  const clearSelection = () => setSelected({});

  const promotedList = Object.values(promoted).filter(
    (p) => p.provider === network && p.promoted
  );

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-100">Browse Products</p>
          {searchResults.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={selectedCount === searchResults.length ? clearSelection : selectAll}
                className="text-[11px] text-gray-400 hover:text-gray-200 transition-colors">
                {selectedCount === searchResults.length ? "Deselect all" : "Select all"}
              </button>
            </div>
          )}
        </div>
        {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={`Search ${network} products…`}
            className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          {searching && (
            <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />
          )}
        </div>

        {searchError && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <AlertCircle className="w-3.5 h-3.5" />
            {searchError}
          </div>
        )}

        {searchResults.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3 pt-1">
            {searchResults.map((p) => (
              <ProductCard
                key={p.externalId}
                product={p}
                promoted={!!promoted[p.externalId]}
                selected={!!selected[p.externalId]}
                onPromote={() => onPromote(p)}
                onUnpromote={() => onUnpromote(p)}
                onToggleSelect={() => toggleSelect(p)}
                busy={!!promoteBusy[p.externalId]}
              />
            ))}
          </div>
        )}

        {!searching && searchQuery && searchResults.length === 0 && !searchError && (
          <p className="text-xs text-gray-600 text-center py-4">No results for "{searchQuery}"</p>
        )}

        {/* Bulk action bar */}
        {selectedCount > 0 && onCreateContent && (
          <div className="flex items-center justify-between bg-brand-900/30 border border-brand-700/40 rounded-lg px-4 py-2.5">
            <span className="text-sm text-brand-200">
              {selectedCount} product{selectedCount > 1 ? "s" : ""} selected
            </span>
            <div className="flex items-center gap-2">
              <button onClick={clearSelection}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors">
                Clear
              </button>
              <button
                onClick={() => onCreateContent(Object.values(selected))}
                disabled={createContentBusy}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                {createContentBusy ? "Queueing…" : "Create Content"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Promoted products */}
      {promotedList.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-brand-400" fill="currentColor" />
            <p className="text-sm font-semibold text-gray-100">
              Promoted Products ({promotedList.length})
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
            {promotedList.map((p) => {
              const lp: LiveProduct = {
                externalId: p.externalId,
                title: p.title,
                price: p.priceAmount,
                currency: p.currency,
                imageUrl: p.imageUrl,
                productUrl: p.productUrl ?? "",
                affiliateUrl: p.affiliateUrl,
                merchantName: p.merchantName,
                category: p.category,
                network,
              };
              return (
                <ProductCard
                  key={p._id}
                  product={lp}
                  promoted
                  onPromote={() => {}}
                  onUnpromote={() => onUnpromote(lp)}
                  busy={!!promoteBusy[p.externalId]}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
